<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { sanitizeObject } from "@/helpers/object";
import { useClientStore } from "@/modules/clients/store/clientStore";
import { SynchronizeFieldType } from "@/modules/clients/types";
import { PersonTitle } from '@/types/person';

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Loader from "@/dsComponents/Loader.vue";
import Modal from "@/dsComponents/modals/Modal.vue";

import SynchronizeField from "@/modules/clients/components/SynchronizeField.vue";

interface Props {
    open: boolean;
    salesforceId: string;
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    contactForm: any;
}

const props = defineProps<Props>();

const emit = defineEmits<(e: "close") => void>();

const clientStore = useClientStore();
const { toastSuccess, toastError } = useDefaultToast();

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const contact = ref<any>(null);
const error = ref<string | null>(null);
const { t } = useI18n();

onMounted(async () => {
    try {
        const contactData = await clientStore.actions.fetchContactBySalesforceId(props.salesforceId);
        contact.value = contactData;
    } catch (e) {
        console.error("[onMounted] Error fetching contact by Salesforce ID:", e);
        const errorMessage = t("page.client.contact.salesforceImport.error");
        toastError(errorMessage);
        error.value = errorMessage;
    }
});

const handleImport = async () => {
    // Format the contact by complete zod validations
    const formattedContact = sanitizeObject(contact.value);
    // Format the companies list and add the current company id
    const companiesList = formattedContact.companiesList
        ? [...formattedContact.companiesList, clientStore.state.company?.id]
        : [clientStore.state.company?.id];
    formattedContact.companiesList = companiesList.filter(Boolean);
    if (formattedContact.companiesList.length === 0) {
        toastError(t("page.client.contact.error.addFailed"));
        return;
    }
    // Format the civility
    if (["m", "m.", "mr", "mr."].includes(formattedContact.civility.toLowerCase())) formattedContact.civility = PersonTitle.Mr;
    if (["mme", "mme.", "mrs"].includes(formattedContact.civility.toLowerCase())) formattedContact.civility = PersonTitle.Mme;
    // biome-ignore lint/style/noNonNullAssertion: Init Biome
    await clientStore.actions.addCompanyContact(clientStore.state.company!.id!, formattedContact);
    toastSuccess(t("page.client.contact.toast.addSucceed"));
    emit("close");
};
</script>

<template>
    <div class="client-salesforce-import">
        <Modal :title="t('page.client.contact.salesforceImport.title')" :open="open" @close="emit('close')">
            <template #default>
                <div class="error-container" v-if="error">
                    <p class="error-message typo-md-medium">{{ error }}</p>
                </div>
                <div class="client-salesforce-import-content" v-else-if="contact">
                    <div class="salesforce-import-details">
                        <div class="section-subtitle typo-md-regular">{{ t("page.client.contact.salesforceImport.detailsSubtitle") }}</div>
                        <h2 class="typo-xl-semibold">{{ t("page.client.contact.salesforceImport.details") }}</h2>
                    </div>
                    <SynchronizeField
                        :label="t('page.client.contact.civility')"
                        :formValue="contactForm.civility"
                        :value="contact.civility"
                        icon="doc-name"
                    />
                    <SynchronizeField
                        :label="t('page.client.contact.lastname')"
                        :formValue="contactForm.lastname"
                        :value="contact.lastname"
                        icon="doc-name"
                    />
                    <SynchronizeField
                        :label="t('page.client.contact.firstname')"
                        :formValue="contactForm.firstname"
                        :value="contact.firstname"
                        icon="doc-name"
                    />
                    <SynchronizeField :label="t('page.client.contact.email')" :formValue="contactForm.email" :value="contact.email" icon="at" />
                    <SynchronizeField
                        :label="t('page.client.contact.role')"
                        :formValue="contactForm.role"
                        :value="contact.role"
                        icon="user"
                        :type="SynchronizeFieldType.ROLE"
                    />
                    <SynchronizeField
                        :label="t('page.client.contact.service')"
                        :formValue="contactForm.service"
                        :value="contact.service"
                        icon="company"
                    />
                    <SynchronizeField :label="t('page.client.contact.job')" :formValue="contactForm.job" :value="contact.job" icon="work" />
                    <SynchronizeField
                        :label="t('page.client.contact.phone')"
                        :formValue="contactForm.phone"
                        :value="contact.phone"
                        icon="phone"
                        :type="SynchronizeFieldType.PHONE"
                    />
                    <SynchronizeField
                        :label="t('page.client.contact.otherPhone')"
                        :formValue="contactForm.otherPhone"
                        :value="contact.otherPhone"
                        icon="phone"
                        :type="SynchronizeFieldType.PHONE"
                    />
                    <SynchronizeField
                        :label="t('page.client.contact.description')"
                        :formValue="contactForm.description"
                        :value="contact.description"
                        icon="info-circle"
                    />
                    <SynchronizeField
                        :label="t('page.client.contact.activeProposalStatus')"
                        :formValue="contactForm.activeProposalStatus"
                        :value="contact.activeProposalStatus"
                        :type="SynchronizeFieldType.ACTIVE_PROPOSAL_STATUS"
                        icon="mail-arrow-right"
                    />
                    <SynchronizeField :label="t('page.client.contact.salesforceId')" :value="contact.salesforceId" icon="salesforce" />
                </div>
                <div class="loading-container" v-else>
                    <Loader />
                </div>
            </template>
            <template #footer>
                <div class="actions-container">
                    <ButtonAlt :label="t('global.actions.cancel')" size="sm" type="neutral" variant="stroke" @click="emit('close')" />
                    <ButtonAlt
                        v-if="!error"
                        :label="t('page.client.contact.salesforceImport.import')"
                        size="sm"
                        type="primary"
                        variant="filled"
                        @click="handleImport"
                    />
                </div>
            </template>
        </Modal>
    </div>
</template>

<style scoped lang="scss">
.error-container {
    display: flex;
    justify-content: center;
    align-items: center;
    .error-message {
        color: $text-error-600;
    }
}
.client-salesforce-import-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xl, 16px);
}
.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
}
.actions-container {
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-xl, 16px);
}
.salesforce-import-details {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: var(--spacing-sm, 6px);
    margin-bottom: var(--spacing-md, 8px);

    .section-subtitle {
        color: var(--text-sub-600, #494e61);
    }
}
</style>
